import { getPromoInfoApi, getPromoTaskListApi, getFormInfo } from 'api/client';
import { normalizePromoTasksList, normalizePromoLotteryList } from 'api/utils';
import { isJSONParsable, objFirstLevelJSONParse } from 'utils/objectManipulate';
import {
    gameTypes,
    checkIsGameHasPromoTask,
    contestGetUgcLinkKey,
    isContestGamesForUserPlayableUgc,
    isPurePrizeGames
} from 'config/promoGameTypes';
import { IS_UGC_FLOW } from 'config/envVariable';
import { fromEntries } from 'utils/core';

export default async function getPromoInfoData(promoUUID, exclude = []) {
    const primaryPromoData = await getPromoInfoApi({
        promoUUID
    });
    const result = await mappingPromoInfoApi2State(primaryPromoData, exclude);
    return result;
}

const mappingPromoInfoApi2State = async (apiData, exclude = []) => {
    let data = {};
    const filterNotNecessaryApiResponse = fromEntries(
        Object.entries(apiData).filter(([apiKey]) => !exclude.includes(apiKey))
    );

    data = getPromoState(filterNotNecessaryApiResponse);
    data = await getFormState(data);
    data = await getPromoTasksState(data);
    data = getLotteriesState(data);
    data = getPrizeState(data);

    if (!exclude.includes('pointPool')) {
        data = getPromoPointPoolInfoState(data);
    }

    if (!exclude.includes('mgmRelation')) {
        data = await getPromoMgmRelationState(data);
    }
    data = await getRelatedTwinsPromoState(data);

    return data;
};

function getPromoState({ promotion, ...rest }) {
    return {
        partner: { ...promotion.partner, ...promotion.partnerSetting },
        sponsor: { ...promotion.sponsor, ...promotion.sponsorSetting },
        promo: promotion.promo,
        promoState: {
            isPurePrizeGame: isPurePrizeGames(promotion.promo.type)
        },
        settingPromo: promotion.promoSetting.Promotions,
        settingPointSystems: promotion.promoSetting.PointSystems,
        settingStyle: objFirstLevelJSONParse(promotion.promoSetting.Styles),
        settingGame: objFirstLevelJSONParse(promotion.promoSetting.Games),
        settingGameStyle: objFirstLevelJSONParse(promotion.promoSetting.GameStyles),
        settingMGMReferral: objFirstLevelJSONParse(promotion.promoSetting.MGMReferral),
        settingEntryValue: objFirstLevelJSONParse(promotion.promoSetting.EntryValues),
        settingTheme: promotion.promoSetting.Themes,
        settingPlan: promotion.planSetting,
        signUpForm: promotion.promoSetting.SignUpForm,
        awardeeForm: promotion.promoSetting?.AwardeeForms?.list
            ? JSON.parse(promotion.promoSetting.AwardeeForms.list)
            : [],
        zines: promotion.promoSetting.SignUpForm,
        lotteryAdmin: promotion?.promoSetting?.LotteryAdmin ?? {},
        lotteryInstant: promotion?.promoSetting?.LotteryInstant ?? {},
        lotteryParticipant: promotion?.promoSetting?.LotteryParticipant ?? {},
        lotteryTasks: promotion?.promoSetting?.LotteryTasks ?? {},
        ...rest
    };
}

function getPromoPointPoolInfoState(data) {
    const { pointPool, ...rest } = data;

    if (!Array.isArray(pointPool) && Object.keys(pointPool).length > 0) {
        for (let pointPoolUuid in pointPool) {
            pointPool[pointPoolUuid].settings = objFirstLevelJSONParse(
                pointPool[pointPoolUuid].settings.Styles
            );
        }
    }

    return {
        ...rest,
        pointSystem: {
            pointPoolInfos: pointPool
        }
    };
}

async function getPromoMgmRelationState(data) {
    const { mgmRelation } = data;
    if (!Array.isArray(mgmRelation) && Object.keys(mgmRelation).length > 0) {
        const { mgmReferral, mgmRole } = mgmRelation;
        const isMGMGame = mgmRole === 'mgmShare' || mgmRole === 'mgmSelf';
        if (Boolean(mgmReferral) && isMGMGame) {
            const { uuid: mgmReferralPromoUUID } = mgmReferral;
            const mgmReferralPromoInfoData = await getPromoInfoApi({
                promoUUID: mgmReferralPromoUUID
            });
            const formattedMgmReferralPromoInfoData = await mappingPromoInfoApi2State(
                mgmReferralPromoInfoData
            );
            return {
                ...data,
                mgmReferral: formattedMgmReferralPromoInfoData
            };
        }
    }

    return {
        ...data,
        mgmReferral: {}
    };
}

async function getPromoTasksState(data) {
    const promo = data.promo;
    if (!checkIsGameHasPromoTask(promo.type)) return data;

    const { payload } = await getPromoTaskListApi({ promoUUID: promo.uuid });
    return {
        ...data,
        promoTasks: await normalizePromoTasksList(payload)
    };
}

function getLotteriesState(data) {
    const {
        lotteryAdmin,
        lotteryInstant,
        lotteryParticipant,
        lotteryTasks: lotteryTasksData,
        lotteries,
        ...rest
    } = data;

    const lotteryState = normalizePromoLotteryList(lotteries);
    const lotteryTasks = isJSONParsable(lotteryTasksData?.uuid)
        ? JSON.parse(lotteryTasksData?.uuid)
        : lotteryTasksData;
    return {
        ...rest,
        lotteryAdmin: Boolean(lotteryAdmin?.uuid),
        lotteryInstant: {
            uuid: lotteryInstant?.uuid,
            ...(lotteryState[lotteryInstant?.uuid] ?? {})
        },
        lotteryParticipant: {
            uuid: lotteryParticipant?.uuid,
            ...(lotteryState[lotteryParticipant?.uuid] ?? {})
        },
        lotteryTasks: Object.entries(lotteryTasks).reduce((obj, [lotteryUuid, taskCondition]) => {
            obj[lotteryUuid] = {
                taskCondition,
                ...(lotteryState[lotteryUuid] ?? {})
            };
            return obj;
        }, {})
    };
}

function getPrizeState(data) {
    const { lotteryPrizes, lotteryParticipant } = data;
    if (lotteryParticipant?.uuid) {
        return {
            ...data,
            lotteryPrizes
        };
    }

    return {
        ...data,
        lotteryPrizes: {}
    };
}

async function getFormState(data) {
    const formId = data?.settingGame?.formId;
    const promoType = data.promo.type;
    // [#86entxjdp] 空 formbased 模組強制不 create form
    if (!formId || isPurePrizeGames(promoType) || promoType === gameTypes.SIGN_FORM) return data;

    const {
        payload: { pages = [], sections = [], fields = [], options = [], settings = {} } = {}
    } = await getFormInfo({ formId });

    return {
        ...data,
        gameFormBased: {
            pages,
            sections,
            fields,
            options,
            settings
        }
    };
}

async function getRelatedTwinsPromoState(primaryPromoData) {
    const {
        promo: { type: primaryGameType } = {},
        settingPromo: primarySettingPromo = {}
    } = primaryPromoData;

    const {
        [contestGetUgcLinkKey(primaryGameType, primarySettingPromo)]: relatedPromoUuid
    } = primarySettingPromo;

    const hasSecondaryPromo = Boolean(relatedPromoUuid);

    if (!hasSecondaryPromo) {
        return primaryPromoData;
    }

    let secondaryPromoData = await getPromoInfoApi({
        promoUUID: relatedPromoUuid
    });

    secondaryPromoData = await mappingPromoInfoApi2State(secondaryPromoData);

    // 有 hasUgcFlow + 有 ugcLinkKeys
    if (isContestGamesForUserPlayableUgc(primaryGameType)) {
        // style setting is saved in ugc module
        primaryPromoData.settingStyle = secondaryPromoData.settingStyle;
        // theme setting is saved in contest module
        secondaryPromoData.settingTheme = primaryPromoData.settingTheme;
        secondaryPromoData.settingPromo = {
            language: primaryPromoData.settingPromo.language,
            ...secondaryPromoData.settingPromo
        };

        if (IS_UGC_FLOW) {
            const secondaryPromoWithoutPartnerAndSponsor = (() => {
                const { partner, sponsor, ...rest } = secondaryPromoData;
                return rest;
            })();
            return {
                ...primaryPromoData,
                relatedTwinsPromo: secondaryPromoWithoutPartnerAndSponsor
            };
        } else {
            const primaryPromoWithoutPartnerAndSponsor = (() => {
                const { partner, sponsor, ...rest } = primaryPromoData;
                return rest;
            })();
            return {
                ...secondaryPromoData,
                relatedTwinsPromo: primaryPromoWithoutPartnerAndSponsor
            };
        }
    }

    // 無hasUgcFlow + 有 ugcLinkKeys
    if (primaryGameType === gameTypes.CONTEST_ADMIN_UPLOAD) {
        primaryPromoData.settingStyle = secondaryPromoData.settingStyle;
        const secondaryPromoWithoutPartnerAndSponsor = (() => {
            const { partner, sponsor, ...rest } = secondaryPromoData;
            return rest;
        })();
        return {
            ...primaryPromoData,
            relatedTwinsPromo: secondaryPromoWithoutPartnerAndSponsor
        };
    }

    return primaryPromoData;
}
